import 'shared-react/lumen/styles.css';

import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';
import GeneralPageMain from 'lumen/components/general/page/main';
import SettingsDrawerNavigationHeader from 'lumen/components/settings/drawer/navigation-header';
import type DestinationSettingsController from 'lumen/controllers/destination/settings';
import hasFeature from 'lumen/helpers/has-feature';
import hasFeatureToggle from 'lumen/helpers/has-feature-toggle';
import mountReact from 'lumen/modifiers/mount-react';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { DaasSettingsSidePanel } from 'shared-react/lumen';

interface DestinationSettingsSignature {
  Args: {
    controller: DestinationSettingsController;
    model: unknown;
  };
}

<template>
  <GeneralPageMain class="navbar-settings">
    {{#if (hasFeatureToggle FEATURE_TOGGLES.REACT_SETTINGS_SIDE_PANEL)}}
      <div class="flex" {{mountReact DaasSettingsSidePanel}}></div>
    {{else}}
      <div
        class="settings-drawer
          {{if
            @controller.isSettingsDrawerOpen
            'settings-drawer--open'
            'settings-drawer--close'
          }}
          "
      >
        <SettingsDrawerNavigationHeader
          @isDrawerOpen={{@controller.isSettingsDrawerOpen}}
          @toggleNavbar={{@controller.toggleNavbar}}
        />
        {{#if @controller.isSettingsDrawerOpen}}
          <ul class="text-300--medium settings-drawer__content flex flex-col">
            {{#if @controller.hasPersonalSettingsGroup}}
              <li class="settings-drawer__group">
                <h4
                  class="settings-drawer__group-title"
                  data-test-id="settings-group-personal"
                >
                  {{t "settings.section.personal_settings"}}
                </h4>
                <ul class="settings-drawer__list">
                  {{#if @controller.access.hasPersonalSettingsTab}}
                    <li>
                      <LinkTo
                        @route="destination.settings.personal"
                        class="settings-drawer__link"
                      >
                        {{t "menu.my_account"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                  {{#if @controller.access.canEditPersonalSettingsPassword}}
                    <li>
                      <LinkTo
                        @route="destination.settings.security"
                        class="settings-drawer__link"
                      >
                        {{t "settings.security.title"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                  {{#if @controller.theme.hasPermissions}}
                    <li>
                      <LinkTo
                        @route="destination.settings.theme"
                        class="settings-drawer__link"
                        data-test-id="settings-item-theme"
                      >
                        {{t "settings.theme.title"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                </ul>
              </li>
            {{/if}}

            {{#if @controller.hasGeneralGroup}}
              <li class="settings-drawer__group">
                <h4
                  class="settings-drawer__group-title"
                  data-test-id="settings-group-general"
                >
                  {{t "settings.general"}}
                </h4>
                <ul class="settings-drawer__list">
                  {{#if @controller.hasUserAccessSettings}}
                    <li>
                      <LinkTo
                        @route="destination.settings.users"
                        class="settings-drawer__link"
                        data-test-id="settings-item-users"
                      >
                        {{t "settings.section.users"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                  {{#if @controller.hasHotelAccessSettings}}
                    <li>
                      <LinkTo
                        @route="destination.settings.hotels"
                        class="settings-drawer__link"
                        data-test-id="link-hotel-access"
                      >
                        {{t "settings.section.hotel_access"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                  {{#if (hasFeature "reports.view.settings")}}
                    <li>
                      <LinkTo
                        @route="destination.settings.reports"
                        class="settings-drawer__link"
                      >
                        {{t "settings.section.scheduled_reports"}}
                      </LinkTo>
                    </li>
                  {{/if}}
                </ul>
              </li>
            {{/if}}
          </ul>
        {{/if}}
      </div>
    {{/if}}
    <div class="content-wrapper main-content">
      {{outlet}}
    </div>
  </GeneralPageMain>
</template> satisfies TOC<DestinationSettingsSignature>;
